/** The API client. One place that knows how a request is authenticated. */

import type { AuthClient } from "./auth.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  /** For R2 uploads, where the body is a file rather than JSON. */
  put<T>(path: string, body: BodyInit, contentType: string): Promise<T>;
}

export function createApiClient(auth: AuthClient | null, base = "/api"): ApiClient {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    contentType = "application/json",
  ): Promise<T> {
    const headers: Record<string, string> = {};
    const token = auth ? await auth.token() : null;
    if (token) headers["Authorization"] = `Bearer ${token}`;

    let payload: BodyInit | undefined;
    if (body !== undefined) {
      if (contentType === "application/json") {
        headers["Content-Type"] = "application/json";
        payload = JSON.stringify(body);
      } else {
        headers["Content-Type"] = contentType;
        payload = body as BodyInit;
      }
    }

    const res = await fetch(`${base}${path}`, { method, headers, body: payload });
    const text = await res.text();
    let parsed: unknown = null;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }

    if (!res.ok) {
      const asRecord = (parsed ?? {}) as Record<string, unknown>;
      throw new ApiError(
        res.status,
        typeof asRecord["error"] === "string" ? asRecord["error"] : "unknown",
        typeof asRecord["message"] === "string" ? asRecord["message"] : res.statusText,
        parsed,
      );
    }
    return parsed as T;
  }

  return {
    get: (path) => request("GET", path),
    post: (path, body) => request("POST", path, body),
    patch: (path, body) => request("PATCH", path, body),
    put: (path, body, contentType) => request("PUT", path, body, contentType),
  };
}
